import { useState } from 'react'
import { Navigate } from 'react-router-dom'
import {
  Title,
  Text,
  Card,
  Button,
  Badge,
  Flex,
  Table,
  TableHead,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
  TextInput,
} from '@tremor/react'

// Native <select> styled to match Tremor inputs. Tremor's Select is a
// Headless UI popover which clashes with the modal overlay z-index in
// some browsers (popover ends up behind the backdrop). Native select
// always works and the role list is small enough that we don't lose
// anything by not having a fancy listbox.
const SELECT_CLASS =
  'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 ' +
  'rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 ' +
  'focus:ring-2 focus:ring-blue-500 focus:border-transparent ' +
  'disabled:opacity-50 disabled:cursor-not-allowed'
import {
  PlusIcon,
  TrashIcon,
  KeyIcon,
  PencilSquareIcon,
  XMarkIcon,
  CheckCircleIcon,
  XCircleIcon,
} from '@heroicons/react/24/outline'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { listUsers, createUser, updateUser, deleteUser, resetUserPassword } from '@/api/users'
import { useAuth } from '@/context/AuthContext'
import type { User, UserRole, UserCreatePayload } from '@/types'

const ROLES: { value: UserRole; label: string }[] = [
  { value: 'admin', label: 'Admin' },
  { value: 'manager', label: 'Manager' },
  { value: 'viewer', label: 'Viewer' },
]

function formatDate(value: string | null | undefined): string {
  if (!value) return '—'
  try {
    return new Date(value).toLocaleString('fr-FR', { dateStyle: 'short', timeStyle: 'short' })
  } catch {
    return '—'
  }
}

function roleColor(role: UserRole): 'red' | 'amber' | 'gray' {
  if (role === 'admin') return 'red'
  if (role === 'manager') return 'amber'
  return 'gray'
}

function extractApiError(error: unknown, fallback: string): string {
  // FastAPI returns either {detail: "..."} or {detail: [{...validation...}]}.
  // Re-use a tiny version of OperatorDetail's formatErrorDetail so the page
  // never renders an object child.
  const e = error as { response?: { data?: { detail?: unknown } }; message?: string }
  const detail = e?.response?.data?.detail
  if (typeof detail === 'string') return detail
  if (Array.isArray(detail) && detail.length > 0) {
    const first = detail[0]
    if (first && typeof first === 'object' && 'msg' in first) return String((first as { msg: unknown }).msg)
  }
  return e?.message || fallback
}

export function Users() {
  const { user: currentUser } = useAuth()
  const queryClient = useQueryClient()
  const [feedback, setFeedback] = useState<{ kind: 'success' | 'error'; text: string } | null>(null)
  const [showCreateModal, setShowCreateModal] = useState(false)
  const [editingUser, setEditingUser] = useState<User | null>(null)
  const [resetPasswordUser, setResetPasswordUser] = useState<User | null>(null)

  // Admin-only page; bounce non-admins. Backend already gates the API.
  if (currentUser && currentUser.role !== 'admin') {
    return <Navigate to="/" replace />
  }

  const { data: users = [], isLoading } = useQuery({
    queryKey: ['users'],
    queryFn: listUsers,
    refetchInterval: 30_000,
  })

  function flash(kind: 'success' | 'error', text: string) {
    setFeedback({ kind, text })
    setTimeout(() => setFeedback(null), 5000)
  }

  const createMutation = useMutation({
    mutationFn: createUser,
    onSuccess: (created) => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      setShowCreateModal(false)
      flash('success', `Utilisateur ${created.email} créé`)
    },
    onError: (error) => flash('error', extractApiError(error, 'Création échouée')),
  })

  const updateMutation = useMutation({
    mutationFn: ({ id, payload }: { id: number; payload: Parameters<typeof updateUser>[1] }) =>
      updateUser(id, payload),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      setEditingUser(null)
      flash('success', 'Utilisateur mis à jour')
    },
    onError: (error) => flash('error', extractApiError(error, 'Mise à jour échouée')),
  })

  const deleteMutation = useMutation({
    mutationFn: deleteUser,
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['users'] })
      flash('success', 'Utilisateur supprimé')
    },
    onError: (error) => flash('error', extractApiError(error, 'Suppression échouée')),
  })

  const resetPasswordMutation = useMutation({
    mutationFn: ({ id, password }: { id: number; password: string }) =>
      resetUserPassword(id, password),
    onSuccess: () => {
      setResetPasswordUser(null)
      flash('success', 'Mot de passe réinitialisé')
    },
    onError: (error) => flash('error', extractApiError(error, 'Réinitialisation échouée')),
  })

  return (
    <div className="p-3 sm:p-6">
      <div className="mb-4 sm:mb-6 flex items-start justify-between gap-3">
        <div>
          <Title>Utilisateurs</Title>
          <Text>Gérer les comptes du tableau de bord (admin uniquement)</Text>
        </div>
        <Button icon={PlusIcon} onClick={() => setShowCreateModal(true)}>
          Nouvel utilisateur
        </Button>
      </div>

      {feedback && (
        <div
          className={`mb-4 px-4 py-3 rounded-md text-sm ${
            feedback.kind === 'success'
              ? 'bg-green-50 text-green-800 dark:bg-green-900/20 dark:text-green-300'
              : 'bg-red-50 text-red-800 dark:bg-red-900/20 dark:text-red-300'
          }`}
        >
          {feedback.text}
        </div>
      )}

      <Card>
        {isLoading ? (
          <Text className="text-gray-500 py-8 text-center">Chargement…</Text>
        ) : users.length === 0 ? (
          <Text className="text-gray-500 py-8 text-center">Aucun utilisateur</Text>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableHeaderCell>Email</TableHeaderCell>
                <TableHeaderCell>Nom</TableHeaderCell>
                <TableHeaderCell>Rôle</TableHeaderCell>
                <TableHeaderCell>Actif</TableHeaderCell>
                <TableHeaderCell>Dernière connexion</TableHeaderCell>
                <TableHeaderCell>Créé</TableHeaderCell>
                <TableHeaderCell className="text-right">Actions</TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {users.map((u) => {
                const isSelf = currentUser?.id === u.id
                return (
                  <TableRow key={u.id}>
                    <TableCell>
                      <Text className="font-medium">{u.email}</Text>
                      {isSelf && <Text className="text-xs text-gray-500">vous</Text>}
                    </TableCell>
                    <TableCell>{u.fullname}</TableCell>
                    <TableCell>
                      <Badge color={roleColor(u.role)}>{u.role}</Badge>
                    </TableCell>
                    <TableCell>
                      {u.is_active ? (
                        <Flex justifyContent="start" className="gap-1 text-green-600">
                          <CheckCircleIcon className="w-4 h-4" />
                          <Text>Actif</Text>
                        </Flex>
                      ) : (
                        <Flex justifyContent="start" className="gap-1 text-gray-500">
                          <XCircleIcon className="w-4 h-4" />
                          <Text>Désactivé</Text>
                        </Flex>
                      )}
                    </TableCell>
                    <TableCell>
                      <Text className="text-sm">{formatDate(u.last_login)}</Text>
                    </TableCell>
                    <TableCell>
                      <Text className="text-sm">{formatDate(u.created_at)}</Text>
                    </TableCell>
                    <TableCell className="text-right">
                      <Flex justifyContent="end" className="gap-1">
                        <Button
                          size="xs"
                          variant="secondary"
                          icon={PencilSquareIcon}
                          onClick={() => setEditingUser(u)}
                        >
                          Modifier
                        </Button>
                        <Button
                          size="xs"
                          variant="secondary"
                          icon={KeyIcon}
                          onClick={() => setResetPasswordUser(u)}
                        >
                          MDP
                        </Button>
                        <Button
                          size="xs"
                          variant="secondary"
                          color="red"
                          icon={TrashIcon}
                          disabled={isSelf || deleteMutation.isPending}
                          onClick={() => {
                            if (confirm(`Supprimer l'utilisateur ${u.email} ?`)) {
                              deleteMutation.mutate(u.id)
                            }
                          }}
                        >
                          Supprimer
                        </Button>
                      </Flex>
                    </TableCell>
                  </TableRow>
                )
              })}
            </TableBody>
          </Table>
        )}
      </Card>

      {showCreateModal && (
        <CreateUserModal
          onClose={() => setShowCreateModal(false)}
          onSubmit={(payload) => createMutation.mutate(payload)}
          isPending={createMutation.isPending}
        />
      )}

      {editingUser && (
        <EditUserModal
          user={editingUser}
          isSelf={currentUser?.id === editingUser.id}
          onClose={() => setEditingUser(null)}
          onSubmit={(payload) =>
            updateMutation.mutate({ id: editingUser.id, payload })
          }
          isPending={updateMutation.isPending}
        />
      )}

      {resetPasswordUser && (
        <ResetPasswordModal
          user={resetPasswordUser}
          onClose={() => setResetPasswordUser(null)}
          onSubmit={(password) =>
            resetPasswordMutation.mutate({ id: resetPasswordUser.id, password })
          }
          isPending={resetPasswordMutation.isPending}
        />
      )}
    </div>
  )
}

interface CreateProps {
  onClose: () => void
  onSubmit: (payload: UserCreatePayload) => void
  isPending: boolean
}

function CreateUserModal({ onClose, onSubmit, isPending }: CreateProps) {
  const [email, setEmail] = useState('')
  const [fullname, setFullname] = useState('')
  const [password, setPassword] = useState('')
  const [role, setRole] = useState<UserRole>('viewer')

  const valid = email.length > 3 && fullname.length > 0 && password.length >= 8

  return (
    <ModalShell title="Nouvel utilisateur" onClose={onClose}>
      <FormField label="Email">
        <TextInput
          type="email"
          value={email}
          onValueChange={setEmail}
          placeholder="user@exemple.com"
        />
      </FormField>
      <FormField label="Nom complet">
        <TextInput value={fullname} onValueChange={setFullname} />
      </FormField>
      <FormField label="Mot de passe initial (>= 8 caractères)">
        <TextInput type="password" value={password} onValueChange={setPassword} />
        <Text className="text-xs text-gray-500 mt-1">
          L'utilisateur sera obligé de le changer à la première connexion.
        </Text>
      </FormField>
      <FormField label="Rôle">
        <select
          value={role}
          onChange={(e) => setRole(e.target.value as UserRole)}
          className={SELECT_CLASS}
        >
          {ROLES.map((r) => (
            <option key={r.value} value={r.value}>
              {r.label}
            </option>
          ))}
        </select>
      </FormField>
      <Flex justifyContent="end" className="gap-2 mt-4">
        <Button variant="secondary" onClick={onClose}>
          Annuler
        </Button>
        <Button
          disabled={!valid || isPending}
          loading={isPending}
          onClick={() => onSubmit({ email, fullname, password, role })}
        >
          Créer
        </Button>
      </Flex>
    </ModalShell>
  )
}

interface EditProps {
  user: User
  isSelf: boolean
  onClose: () => void
  onSubmit: (payload: { email?: string; fullname?: string; role?: UserRole; is_active?: boolean }) => void
  isPending: boolean
}

function EditUserModal({ user, isSelf, onClose, onSubmit, isPending }: EditProps) {
  const [email, setEmail] = useState(user.email)
  const [fullname, setFullname] = useState(user.fullname)
  const [role, setRole] = useState<UserRole>(user.role)
  const [isActive, setIsActive] = useState(user.is_active)

  return (
    <ModalShell title={`Modifier ${user.email}`} onClose={onClose}>
      <FormField label="Email">
        <TextInput type="email" value={email} onValueChange={setEmail} />
      </FormField>
      <FormField label="Nom complet">
        <TextInput value={fullname} onValueChange={setFullname} />
      </FormField>
      <FormField label="Rôle">
        <select
          value={role}
          onChange={(e) => setRole(e.target.value as UserRole)}
          disabled={isSelf}
          className={SELECT_CLASS}
        >
          {ROLES.map((r) => (
            <option key={r.value} value={r.value}>
              {r.label}
            </option>
          ))}
        </select>
        {isSelf && (
          <Text className="text-xs text-gray-500 mt-1">
            Vous ne pouvez pas vous retirer le rôle admin.
          </Text>
        )}
      </FormField>
      <FormField label="Statut">
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={isActive}
            onChange={(e) => setIsActive(e.target.checked)}
            disabled={isSelf}
            className="rounded"
          />
          <span>Compte actif</span>
        </label>
      </FormField>
      <Flex justifyContent="end" className="gap-2 mt-4">
        <Button variant="secondary" onClick={onClose}>
          Annuler
        </Button>
        <Button
          disabled={isPending}
          loading={isPending}
          onClick={() =>
            onSubmit({
              email: email !== user.email ? email : undefined,
              fullname: fullname !== user.fullname ? fullname : undefined,
              role: role !== user.role ? role : undefined,
              is_active: isActive !== user.is_active ? isActive : undefined,
            })
          }
        >
          Enregistrer
        </Button>
      </Flex>
    </ModalShell>
  )
}

interface ResetProps {
  user: User
  onClose: () => void
  onSubmit: (password: string) => void
  isPending: boolean
}

function ResetPasswordModal({ user, onClose, onSubmit, isPending }: ResetProps) {
  const [password, setPassword] = useState('')

  return (
    <ModalShell title={`Réinitialiser le mot de passe — ${user.email}`} onClose={onClose}>
      <Text className="text-sm text-gray-600 dark:text-gray-400 mb-3">
        L'utilisateur sera obligé de changer ce mot de passe à sa prochaine connexion.
      </Text>
      <FormField label="Nouveau mot de passe (>= 8 caractères)">
        <TextInput type="password" value={password} onValueChange={setPassword} />
      </FormField>
      <Flex justifyContent="end" className="gap-2 mt-4">
        <Button variant="secondary" onClick={onClose}>
          Annuler
        </Button>
        <Button
          disabled={password.length < 8 || isPending}
          loading={isPending}
          onClick={() => onSubmit(password)}
        >
          Réinitialiser
        </Button>
      </Flex>
    </ModalShell>
  )
}

interface ModalShellProps {
  title: string
  onClose: () => void
  children: React.ReactNode
}

function ModalShell({ title, onClose, children }: ModalShellProps) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-3">
      <Card className="w-full sm:max-w-md max-h-[90vh] overflow-y-auto">
        <Flex justifyContent="between" alignItems="center" className="mb-4">
          <Title>{title}</Title>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-700 dark:hover:text-gray-300">
            <XMarkIcon className="w-5 h-5" />
          </button>
        </Flex>
        <div className="space-y-3">{children}</div>
      </Card>
    </div>
  )
}

interface FormFieldProps {
  label: string
  children: React.ReactNode
}

function FormField({ label, children }: FormFieldProps) {
  return (
    <div>
      <Text className="text-sm font-medium mb-1">{label}</Text>
      {children}
    </div>
  )
}
